import type { ReactNode } from "react"
import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Drawer, useMediaQuery, useTheme } from "@mui/material"

import type { AlertSettingAccordion } from "types/alerts.types"

import useEntityAlertTypes from "helpers/hooks/useEntityAlertTypes"
import ConfirmationPopup from "widgets/common/ConfirmationPopup"
import Spinner from "widgets/common/Spinner"
import AddAlertSetting from "./AddAlertSetting"
import UpdateAlertSetting from "./UpdateAlertSetting"
import {
  type AlertSettingEntity,
  AlertSettingDrawerContext,
} from "./AlertSettingDrawerContext"

type DrawerContent =
  | { mode: "create"; entity: AlertSettingEntity; entityId: number }
  | { mode: "edit"; alertSetting: AlertSettingAccordion }

const CreateAlertSetting = ({
  entity,
  entityId,
  onClose,
}: {
  entity: AlertSettingEntity
  entityId: number
  onClose: () => void
}) => {
  const { alertTypes, isLoading } = useEntityAlertTypes(
    entity,
    entity === "device" ? entityId : undefined,
  )

  if (isLoading) return <Spinner />
  return (
    <AddAlertSetting
      types={alertTypes ?? []}
      entity={entity}
      entityId={entityId}
      onClose={onClose}
    />
  )
}

const EditAlertSetting = ({
  alertSetting,
  onClose,
}: {
  alertSetting: AlertSettingAccordion
  onClose: () => void
}) => {
  const { alertTypes, isLoading } = useEntityAlertTypes(
    alertSetting.entity,
    alertSetting.entity === "device" ? alertSetting.less_id : undefined,
  )

  if (isLoading) return <Spinner />
  return (
    <UpdateAlertSetting
      selectedAlert={alertSetting}
      types={alertTypes ?? []}
      entity={alertSetting.entity}
      onClose={onClose}
    />
  )
}

export const AlertSettingDrawerProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"))
  const [open, setOpen] = useState(false)
  // Kept after closing so the content stays in place while the drawer slides out.
  const [content, setContent] = useState<DrawerContent | null>(null)
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false)
  // Reported by the open form, so closing from outside it can ask first.
  const isDirty = useRef(false)

  const show = useCallback((nextContent: DrawerContent) => {
    isDirty.current = false
    setContent(nextContent)
    setOpen(true)
  }, [])
  const openCreate = useCallback(
    (entity: AlertSettingEntity, entityId: number) =>
      show({ mode: "create", entity, entityId }),
    [show],
  )
  const openEdit = useCallback(
    (alertSetting: AlertSettingAccordion) => show({ mode: "edit", alertSetting }),
    [show],
  )
  const close = useCallback(() => {
    setIsDiscardConfirmOpen(false)
    setOpen(false)
  }, [])
  const requestClose = useCallback(() => {
    if (isDirty.current) setIsDiscardConfirmOpen(true)
    else close()
  }, [close])
  const setDirty = useCallback((dirty: boolean) => {
    isDirty.current = dirty
  }, [])
  const value = useMemo(
    () => ({ openCreate, openEdit, requestClose, setDirty }),
    [openCreate, openEdit, requestClose, setDirty],
  )

  return (
    <AlertSettingDrawerContext.Provider value={value}>
      {children}
      <Drawer
        anchor="right"
        open={open}
        onClose={requestClose}
        PaperProps={{
          sx: {
            width: isSmall ? "100%" : 740,
            maxWidth: "100%",
          },
        }}
      >
        {content?.mode === "create" && (
          <CreateAlertSetting
            entity={content.entity}
            entityId={content.entityId}
            onClose={close}
          />
        )}
        {content?.mode === "edit" && (
          <EditAlertSetting alertSetting={content.alertSetting} onClose={close} />
        )}
      </Drawer>
      <ConfirmationPopup
        open={isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        description={t("generic.ARE_YOU_SURE_DISCARD")}
        mainButtonText={t("generic.DISCARD_CHANGES")}
        onConfirm={close}
        isLoading={false}
        isDeletePopup
      />
    </AlertSettingDrawerContext.Provider>
  )
}
